import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { api, apiError } from "../lib/api";
import { PageHeader, Card, Btn, Table, Td, Empty, Badge } from "../components/UI";
import { CreditCard, Users, ClipboardList, CalendarClock } from "lucide-react";
import { toast } from "sonner";

const Stat = ({ icon: Icon, label, value, tone = "bg-[#EFEBE0] text-[#4E342E]" }) => (
  <div className="flex items-center gap-4 rounded-2xl border border-[#EFEBE0] bg-white p-5 flex-1 min-w-[200px]">
    <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${tone}`}><Icon size={20} /></span>
    <div><div className="font-heading text-xl font-bold text-[#3E2723]">{value}</div><div className="text-xs text-[#7B6157]">{label}</div></div>
  </div>
);

export default function Billing() {
  const [data, setData] = useState(null);
  const [paying, setPaying] = useState(null);
  useEffect(() => { api.get("/billing/summary").then((r) => setData(r.data)).catch((e) => toast.error(apiError(e))); }, []);
  if (!data) return <div className="py-20 text-center text-[#84675B]">Loading billing…</div>;

  const lic = data.tenant.license || {};
  const expired = lic.expires_at && lic.expires_at < dayjs().format("YYYY-MM-DD");

  const pay = async (inv) => {
    setPaying(inv.id);
    try {
      const r = await api.post(`/billing/invoices/${inv.id}/checkout`, { origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (err) { toast.error(apiError(err)); setPaying(null); }
  };

  return (
    <div data-testid="billing-page">
      <PageHeader title="Billing & Subscription" subtitle={`${data.tenant.name} - plan, license & invoices`} />
      {expired && (
        <div className="mb-5 rounded-xl bg-[#FFEBEE] border border-[#FFCDD2] px-4 py-3 text-sm text-[#C62828]" data-testid="license-expired-banner">
          Your license expired on {lic.expires_at}. Pay your latest invoice below to renew instantly.
        </div>
      )}
      <div className="flex flex-wrap gap-4 mb-6">
        <Stat icon={CreditCard} label="Current plan" value={data.plan ? `${data.plan.name} - $${data.plan.price_monthly}/mo` : "-"} />
        <Stat icon={CalendarClock} label="License expires" value={lic.expires_at || "-"} tone={expired ? "bg-[#FFEBEE] text-[#C62828]" : "bg-[#E8F5E9] text-[#2E7D32]"} />
        <Stat icon={Users} label="Staff seats in use" value={`${data.usage.users}${data.plan?.max_users ? ` / ${data.plan.max_users}` : ""}`} />
        <Stat icon={ClipboardList} label="Clients" value={`${data.usage.clients}${data.plan?.max_clients ? ` / ${data.plan.max_clients}` : ""}`} />
      </div>
      <Card>
        <h3 className="font-heading font-bold text-[#3E2723] p-5 pb-2">Invoices</h3>
        <Table headers={["Period", "Plan", "Amount", "Status", "Paid At", ""]}>
          {data.invoices.map((i) => (
            <tr key={i.id} data-testid={`billing-invoice-${i.id}`}>
              <Td className="font-semibold">{i.period}</Td>
              <Td>{i.plan_name}</Td>
              <Td className="tabular-nums">${i.amount?.toLocaleString()}</Td>
              <Td><Badge value={i.status === "paid" ? "completed" : "pending"} /> <span className="text-xs text-[#7B6157] ml-1">{i.status}</span></Td>
              <Td>{i.paid_at ? dayjs(i.paid_at).format("MM/DD/YYYY") : "-"}</Td>
              <Td>
                {i.status !== "paid" && (
                  <Btn data-testid={`pay-invoice-${i.id}`} className="rounded-lg text-xs px-4 py-2" onClick={() => pay(i)} disabled={paying === i.id}>
                    <CreditCard size={13} /> {paying === i.id ? "Redirecting…" : "Pay with Stripe"}
                  </Btn>
                )}
              </Td>
            </tr>
          ))}
        </Table>
        {data.invoices.length === 0 && <Empty text="No invoices yet - Cocoa EHR will issue your monthly invoice here" />}
      </Card>
    </div>
  );
}
